import { config } from './config.js';

let RAPIER;
export let physicsWorld = null;

// Performance monitoring
let physicsStepTime = 0;
let physicsStepCount = 0;

export async function loadRapier() {
    RAPIER = await import('https://cdn.skypack.dev/@dimforge/rapier3d-compat@0.11.2');
    await RAPIER.init();
    return RAPIER;
}

export function setupPhysics() {
    // Tworzenie świata fizycznego
    physicsWorld = new RAPIER.World({ x: 0, y: config.physics.gravity, z: 0 });
    
    // ✅ OPTYMALIZACJA 1: Redukcja iteracji solwera
    // Mniej iteracji = szybsze obliczenia, ale mniej stabilna fizyka
    // Dla Twojej aplikacji (bez precyzyjnych stacków) 2 iteracje wystarczą
    physicsWorld.integrationParameters.numSolverIterations = 2; // domyślnie 4
    physicsWorld.integrationParameters.numAdditionalFrictionIterations = 1; // domyślnie 4
    
    // ✅ OPTYMALIZACJA 2: Fixed timestep dla przewidywalności
    // Zapobiega "spiral of death" przy niskich FPS
    physicsWorld.timestep = 1/60; // 60 FPS
    
    // ✅ OPTYMALIZACJA 3: Optymalizacja broad-phase collision detection
    // Większe marginy = mniej precyzyjne, ale szybsze
    physicsWorld.integrationParameters.contactDamping = 0.01;
    physicsWorld.integrationParameters.jointDamping = 0.01;
    
    // ✅ OPTYMALIZACJA 4: Włącz sleeping dla nieaktywnych obiektów
    // Obiekty które się nie ruszają są "usypiane" i nie są symulowane
    physicsWorld.integrationParameters.allowedLinearError = 0.001;
    physicsWorld.integrationParameters.maxCcdSubsteps = 1; // dla CCD (Continuous Collision Detection)
    
    // Tworzenie podłogi
    const floorBody = physicsWorld.createRigidBody(
        RAPIER.RigidBodyDesc.fixed().setTranslation(0, -5, 0)
    );

    const floorCol = RAPIER.ColliderDesc.cuboid(5000, 5, 5000)
        .setRestitution(0.2)
        .setFriction(0.5)
        .setDensity(1000);

    physicsWorld.createCollider(floorCol, floorBody);
    
    console.log('Physics world initialized with optimizations:', {
        solverIterations: physicsWorld.integrationParameters.numSolverIterations,
        frictionIterations: physicsWorld.integrationParameters.numAdditionalFrictionIterations,
        timestep: physicsWorld.timestep
    });

    return physicsWorld;
}

// ✅ OPTYMALIZACJA 5: Stepped physics update z accumulator
// Zapewnia stały timestep niezależnie od framerate
let accumulator = 0;
const FIXED_TIMESTEP = 1/60;
const MAX_SUBSTEPS = 3; // Maksymalna liczba substeps na klatkę

export function stepPhysics(deltaTime) {
    const startTime = performance.now();
    
    // Ogranicz deltaTime żeby uniknąć spiral of death
    const clampedDelta = Math.min(deltaTime, 0.1);
    accumulator += clampedDelta;
    
    let steps = 0;
    
    // Wykonaj tyle kroków ile potrzeba, ale nie więcej niż MAX_SUBSTEPS
    while (accumulator >= FIXED_TIMESTEP && steps < MAX_SUBSTEPS) {
        physicsWorld.step();
        accumulator -= FIXED_TIMESTEP;
        steps++;
    }
    
    // Jeśli accumulator jest za duży, resetuj go (zapobiega spiral of death)
    if (accumulator > FIXED_TIMESTEP * 2) {
        accumulator = 0;
    }
    
    // Performance monitoring
    physicsStepTime += performance.now() - startTime;
    physicsStepCount++;
    
    // Log co sekundę
    if (physicsStepCount >= 60) {
        const avgStepTime = physicsStepTime / physicsStepCount;
        if (avgStepTime > 5) { // Jeśli > 5ms średnio
            console.warn(`Physics step taking ${avgStepTime.toFixed(2)}ms on average`);
        }
        physicsStepTime = 0;
        physicsStepCount = 0;
    }
    
    return steps;
}

export function getRapier() {
    return RAPIER;
}

// ✅ NOWA FUNKCJA: Bulk sleep check
// Sprawdza czy obiekty śpią bez iteracji przez wszystkie
export function areObjectsSleeping(bodies) {
    if (!bodies || bodies.length === 0) return false;
    
    let sleepingCount = 0;
    for (const body of bodies) {
        if (body && body.isSleeping()) {
            sleepingCount++;
        }
    }
    
    return sleepingCount / bodies.length > 0.8; // 80% śpi
}

// ✅ NOWA FUNKCJA: Force wake objects in radius
// Budzi obiekty w promieniu (np. po eksplozji)
export function wakeObjectsInRadius(position, radius) {
    if (!physicsWorld) return;
    
    const bodies = physicsWorld.bodies;
    bodies.forEach(body => {
        const translation = body.translation();
        const dx = translation.x - position.x;
        const dy = translation.y - position.y;
        const dz = translation.z - position.z;
        const distSq = dx * dx + dy * dy + dz * dz;
        
        if (distSq < radius * radius) {
            body.wakeUp();
        }
    });
}

// ✅ NOWA FUNKCJA: Get physics stats
export function getPhysicsStats() {
    if (!physicsWorld) return null;
    
    return {
        activeRigidBodies: physicsWorld.bodies.len(),
        activeColliders: physicsWorld.colliders.len(),
        timestep: physicsWorld.timestep,
        gravity: physicsWorld.gravity
    };
}